/**
 * ProductCategoryPage — 제품 카테고리 관리 (W19-C1, SC1 flat list 패턴).
 *
 * 【역할】
 * - tb_product_category 마스터 — 계층 parent_id, sort_order, is_active 토글.
 * - 제품 등록/조회 시 카테고리 선택 소스.
 *
 * 【주요 기능】
 * - 조회: keyword + isActive 필터 (product_category/searchList)
 * - 추가 (Modal Form with parent 선택) / 편집 (행 직접) / 삭제 (soft delete)
 * - 저장: C/U/D 일괄 (product_category/save)
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useLabel (다국어)
 * - useI18nTickStore (locale 전환 re-render)
 *
 * 【Backend RPC serviceName】
 * - product_category/searchList (search)
 * - product_category/save (save)
 *
 * 【DataSet 구조】
 * - ds_category: (PK categoryId, 변경추적 C/U/D)
 *
 * 【사용처】 router/index.tsx — `{ path: 'product-category' }`
 * 【포팅 원본】 (없음 — Wave 15 백엔드 있었으나 프론트 미구현. Wave 19 에서 이식.)
 */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Form, Modal, Input, Select, InputNumber, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'
import RowStateTag from '@/components/common/RowStateTag'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'

interface CategoryRow {
  categoryId?: number
  categoryCode?: string
  categoryName?: string
  parentId?: number | null
  sortOrder?: number
  isActive?: number
}

/**
 * ProductCategoryPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ProductCategoryPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_category = $createDataSet<CategoryRow>([])
  const [searchForm] = Form.useForm()
  const [editOpen, setEditOpen] = useState(false)
  const [editForm] = Form.useForm<CategoryRow>()
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)

  const fn_search = async () => {
    setLoading(true)
    const res = await $transaction({
      id: 'cat-search',
      service: 'product_category/searchList',
      action: 'search',
      inds: { ds_search: searchForm.getFieldsValue() },
      outds: { ds_category }
    })
    setLoading(false)
    if (res.success) setSelectedKeys([])
  }

  const fn_save = async () => {
    if (!ds_category.isDirty) return $msg.byId('MSG_NO_CHANGES')
    const res = await $transaction({
      id: 'cat-save',
      service: 'product_category/save',
      action: 'save',
      inds: { ds_category }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search()
    }
  }

  const fn_add = () => {
    editForm.resetFields()
    editForm.setFieldsValue({ isActive: 1, sortOrder: 0, parentId: null })
    setEditOpen(true)
  }

  const fn_editSubmit = async () => {
    const v = await editForm.validateFields()
    ds_category.addRow(v)
    setEditOpen(false)
  }

  const fn_delete = () => {
    if (!selectedKeys.length) return $msg.byId('MSG_SELECT_DELETE_ROW')
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      for (const key of selectedKeys) {
        const idx = ds_category.rows.findIndex((r) => r._rowId === key)
        if (idx >= 0) ds_category.deleteRow(idx)
      }
      setSelectedKeys([])
    })
  }

  useEffect(() => {
    fn_search()
  }, [])

  const visibleRows = ds_category.getVisibleRows()

  // 부모 카테고리 선택용 옵션 (활성 카테고리만)
  const parentOptions = useMemo(
    () =>
      ds_category.rows
        .filter((r) => r._state !== 'D' && r.data?.categoryId)
        .map((r) => ({
          label: `${r.data?.categoryCode} - ${r.data?.categoryName}`,
          value: r.data?.categoryId as number
        })),
    [ds_category.rows]
  )

  const columns: ColumnsType<(typeof visibleRows)[number]> = [
    {
      title: t('COL_CATEGORY_CODE', '카테고리코드'),
      dataIndex: ['data', 'categoryCode'],
      width: 140
    },
    {
      title: t('COL_CATEGORY_NAME', '카테고리명'),
      dataIndex: ['data', 'categoryName'],
      width: 220
    },
    {
      title: t('COL_PARENT', '상위'),
      dataIndex: ['data', 'parentId'],
      width: 140,
      render: (pid: number | null) => {
        if (!pid) return <Tag>{t('LBL_ROOT', 'ROOT')}</Tag>
        const p = ds_category.rows.find((r) => r.data?.categoryId === pid)
        return p?.data?.categoryName ?? pid
      }
    },
    { title: t('COL_SORT_ORDER', '정렬'), dataIndex: ['data', 'sortOrder'], width: 80 },
    {
      title: t('COL_IS_ACTIVE', '사용여부'),
      dataIndex: ['data', 'isActive'],
      width: 100,
      render: (v) =>
        v === 1 ? (
          <Tag color="green">{t('LBL_YES', '사용')}</Tag>
        ) : (
          <Tag>{t('LBL_NO', '미사용')}</Tag>
        )
    },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 70,
      render: (s: string) => <RowStateTag state={s} />
    }
  ]

  return (
    <>
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <FormInput
                  placeholder={t('LBL_CATEGORY_CODE_NAME', '카테고리코드/명')}
                  style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item name="isActive" label={t('LBL_IS_ACTIVE', '사용여부')}>
                <Select
                  allowClear
                  style={{ width: 120 }}
                  options={[
                    { label: t('LBL_YES', '사용'), value: 1 },
                    { label: t('LBL_NO', '미사용'), value: 0 }
                  ]}
                />
              </Form.Item>
            </>
          )
        }}
        toolbar={{
          onSearch: fn_search,
          onAdd: fn_add,
          onDelete: fn_delete,
          onSave: fn_save,
          onCancel: fn_search,
          loading,
          dirty: ds_category.isDirty
        }}
      >
        <Alert
          type="info"
          showIcon
          message={t(
            'MSG_CATEGORY_INFO',
            '제품 카테고리 — 제품 등록 시 참조되는 트리 마스터입니다.'
          )}
          style={{ flexShrink: 0, marginBottom: 8 }}
        />
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys)
          }}
        />
      </AppPage>

      <Modal
        open={editOpen}
        title={t('TTL_ADD_CATEGORY', '카테고리 추가')}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="categoryCode"
            label={t('LBL_CATEGORY_CODE', '카테고리코드')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="categoryName"
            label={t('LBL_CATEGORY_NAME', '카테고리명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label={t('LBL_PARENT_CATEGORY', '상위 카테고리')}>
            <Select allowClear options={parentOptions} placeholder={t('LBL_ROOT', 'ROOT')} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('LBL_SORT_ORDER', '정렬순서')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label={t('LBL_IS_ACTIVE', '사용여부')}>
            <Select
              options={[
                { label: t('LBL_YES', '사용'), value: 1 },
                { label: t('LBL_NO', '미사용'), value: 0 }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
